import type { AztecAddress } from "@aztec/aztec.js";
import type { L2ToL1MembershipWitness } from "@aztec/stdlib/messaging";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Hex } from "@turnstile-portal/turnstile.js";
import { toast } from "sonner";
import { type Address, ContractFunctionExecutionError, parseUnits } from "viem";
import { useConfig } from "wagmi";
import { switchChain } from "wagmi/actions";
import { claimDepositRequest } from "../actions/claim";
import { depositRequest } from "../actions/deposit";
import { completeL1Withdrawal, initiateL2Withdrawal } from "../actions/withdraw";
import { type BridgeType, L1_CHAIN_ID } from "../components/bridge/bridgeConfig";
import { getL1PortalAddr } from "../lib/addresses";
import { storeBridgeTx, storeBridgeTxFailed, updateUnclaimedTx } from "../state/bridgeTxHistory";
import { storeManualClaimTx } from "../state/manualClaim";
import type {
  BridgeClaimParams,
  BridgeTxClaimedFailed,
  BridgeTxClaimedSuccess,
  BridgeTxUnclaimedFailed,
  BridgeTxUnclaimedSuccess,
  ManualClaimParams,
  ManualClaimTxFail,
  ManualClaimTxSuccess,
} from "../state/types";

export type BridgeFormData = {
  bridgeType: BridgeType;
  l1AccountAddress: Address;
  l2AccountAddress: AztecAddress;
  sourceToken: Address;
  tokenDecimals: number;
  tokenSymbol: string;
  transferAmount: string;
};

// Define the parameter types
type DepositParams = {
  bridgeType: "deposit";
  l1AccountAddress: Address;
  l2AccountAddress: AztecAddress;
  sourceToken: Address;
  tokenDecimals: number;
  tokenSymbol: string;
  transferAmount: string;
};

type WithdrawParams = {
  bridgeType: "withdraw";
  l1AccountAddress: Address;
  l2AccountAddress: AztecAddress;
  sourceToken: Address;
  tokenDecimals: number;
  tokenSymbol: string;
  transferAmount: string;
};

// Define the return data types
type DepositReturnData = {
  bridgeType: "deposit";
  txHash: `0x${string}`;
  hash: `0x${string}`;
  l2BlockNumber: number;
  index: bigint;
  senderAddress: Address;
  recipientAddress: Address;
  amount: bigint;
};

type WithdrawReturnData = {
  bridgeType: "withdraw";
  txHash: `0x${string}`;
  l2BlockNumber: number;
  witness: L2ToL1MembershipWitness;
  message: Hex;
  withdrawData: `0x${string}`;
  senderAddress: Address;
  recipientAddress: Address;
  amount: bigint;
};

export function useBridgeMutation({
  onSuccess,
  onError,
  onTxSubmitted,
}: {
  onSuccess: (txData: BridgeTxUnclaimedSuccess) => void;
  onError: (txData: BridgeTxUnclaimedFailed) => void;
  onTxSubmitted?: (txHash: string) => void;
}) {
  const config = useConfig();
  const queryClient = useQueryClient();

  return useMutation<DepositReturnData | WithdrawReturnData, Error, BridgeFormData & { startWaiting: () => void }>({
    mutationFn: async ({
      l1AccountAddress,
      l2AccountAddress,
      tokenDecimals,
      transferAmount,
      bridgeType,
      sourceToken,

      startWaiting,
    }) => {
      // Convert amount using token decimals
      const amount = parseUnits(transferAmount.trim(), tokenDecimals);

      if (bridgeType === "deposit") {
        // This is a must!
        // Switch to L1 chain before deposit
        await switchChain(config, { chainId: L1_CHAIN_ID });

        const recipientAddress = l2AccountAddress.toString() as Address;

        const { txHash, hash, l2BlockNumber, index } = await depositRequest(
          getL1PortalAddr(),
          sourceToken,
          recipientAddress,
          amount,
          startWaiting,
        );

        onTxSubmitted?.(txHash);

        return {
          bridgeType,
          txHash: txHash as `0x${string}`,
          senderAddress: l1AccountAddress,
          recipientAddress: recipientAddress,
          amount,
          l2BlockNumber,
          hash,
          index,
        };
      }

      const { txHash, l2BlockNumber, witness, message, withdrawData } = await initiateL2Withdrawal(
        sourceToken,
        l1AccountAddress,
        amount,
        startWaiting,
      );

      onTxSubmitted?.(txHash);

      return {
        bridgeType,
        txHash: txHash as `0x${string}`,
        senderAddress: l2AccountAddress.toString() as Address,
        recipientAddress: l1AccountAddress,
        amount,
        l2BlockNumber,
        witness,
        message,
        withdrawData,
      };
    },
    onError: (error: Error, variables: DepositParams | WithdrawParams) => {
      console.error(error);

      const id = crypto.randomUUID();
      const timestamp = Date.now();

      const txData: BridgeTxUnclaimedFailed =
        variables.bridgeType === "deposit"
          ? ({
              bridgeType: "deposit",
              state: "unclaimed",
              txStatus: "failed",
              id,
              timestamp,
              errorMessage: (error as ContractFunctionExecutionError).message,
              bridgeFormData: variables,
            } as const)
          : ({
              bridgeType: "withdraw",
              state: "unclaimed",
              txStatus: "failed",
              id,
              errorMessage: (error as ContractFunctionExecutionError).message,
              timestamp,
              bridgeFormData: variables,
            } as const);

      storeBridgeTxFailed(txData);

      onError(txData);
    },
    onSuccess: (data: DepositReturnData | WithdrawReturnData, variables: DepositParams | WithdrawParams) => {
      const id = crypto.randomUUID();
      const timestamp = Date.now();

      const txData: BridgeTxUnclaimedSuccess =
        data.bridgeType === "deposit"
          ? ({
              bridgeType: "deposit",
              state: "unclaimed",
              txStatus: "success",
              id,
              timestamp,
              txHash: data.txHash,
              senderAddress: data.senderAddress,
              recipientAddress: data.recipientAddress,
              amount: data.amount,
              tokenAddress: variables.sourceToken,
              tokenSymbol: variables.tokenSymbol,
              tokenDecimals: variables.tokenDecimals,
              hash: data.hash,
              l2BlockNumber: data.l2BlockNumber,
              index: data.index,
            } as const)
          : ({
              bridgeType: "withdraw",
              state: "unclaimed",
              txStatus: "success",
              id,
              timestamp,
              txHash: data.txHash,
              senderAddress: data.senderAddress,
              recipientAddress: data.recipientAddress,
              amount: data.amount,
              tokenAddress: variables.sourceToken,
              tokenSymbol: variables.tokenSymbol,
              tokenDecimals: variables.tokenDecimals,
              l2BlockNumber: data.l2BlockNumber,
              witness: data.witness,
              message: data.message,
              withdrawData: data.withdrawData,
            } as const);

      storeBridgeTx(txData);

      onSuccess(txData);

      queryClient.invalidateQueries({ queryKey: ["l2AccountBalance"] });
    },
  });
}

export function useBridgeClaimMutation({
  onSuccess,
  onError,
}: {
  onSuccess: (claimedTxData: BridgeTxClaimedSuccess) => void;
  onError: (txData: BridgeTxClaimedFailed) => void;
}) {
  const config = useConfig();
  const queryClient = useQueryClient();

  return useMutation<string, Error, BridgeClaimParams>({
    mutationFn: async (claimData) => {
      if (claimData.bridgeType === "deposit") {
        console.debug("Claiming deposit request with data:", claimData);

        return await claimDepositRequest(
          claimData.tokenAddress,
          claimData.recipientAddress,
          claimData.amount,
          Number(claimData.l2BlockNumber),
          claimData.index,
        );
      }

      // This is a must!
      // Switch to L1 chain before withdraw claim
      await switchChain(config, { chainId: L1_CHAIN_ID });

      return await completeL1Withdrawal(
        claimData.tokenAddress,
        claimData.l2BlockNumber,
        claimData.witness,
        claimData.withdrawData,
      );
    },
    onSuccess: (claimTxHash: string, claimData: BridgeClaimParams) => {
      const claimTimestamp = Date.now();
      const claimedTxData = updateUnclaimedTx(claimData.id, claimTxHash as `0x${string}`, claimTimestamp);

      queryClient.invalidateQueries({ queryKey: ["contract_events"] });

      if (claimedTxData) {
        onSuccess(claimedTxData);
      }
    },
    onError: (error: Error, claimData: BridgeClaimParams) => {
      console.error(error);
      toast.error("Claim failed", { description: (error as ContractFunctionExecutionError).shortMessage });

      const id = crypto.randomUUID();
      const timestamp = Date.now();

      const txData: BridgeTxClaimedFailed = {
        bridgeType: claimData.bridgeType,
        state: "claimed",
        txStatus: "failed",
        id,
        timestamp,
        errorMessage: (error as ContractFunctionExecutionError).message,
        bridgeClaimParams: { ...claimData, isManualClaim: false },
      };

      storeBridgeTxFailed(txData);

      onError(txData);
    },
  });
}

export function useManualClaimMutation({
  onSuccess,
  onError,
}: {
  onSuccess?: (claimTx: ManualClaimTxSuccess) => void;
  onError?: (claimTx: ManualClaimTxFail) => void;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation<string, Error, ManualClaimParams>({
    mutationFn: async (claimParams) => {
      if (claimParams.bridgeType === "deposit") {
        return await claimDepositRequest(
          claimParams.tokenAddress,
          claimParams.recipientAddress,
          claimParams.amount,
          Number(claimParams.l2BlockNumber),
          claimParams.index,
        );
      }

      return await completeL1Withdrawal(
        claimParams.tokenAddress,
        claimParams.l2BlockNumber,
        claimParams.witness,
        claimParams.withdrawData,
      );
    },
    onSuccess: (claimTxHash: string, claimParams: ManualClaimParams) => {
      storeManualClaimTx(claimParams);

      queryClient.invalidateQueries({ queryKey: ["contract_events"] });

      onSuccess?.({
        ...claimParams,
        claimTxHash: claimTxHash as `0x${string}`,
        success: true,
        claimTimestamp: Date.now(),
      });
    },
    onError: (error: Error, claimParams: ManualClaimParams) => {
      console.error(error);

      const errorMessage =
        error instanceof ContractFunctionExecutionError && "shortMessage" in error ? error.shortMessage : error.message;

      onError?.({
        ...claimParams,
        success: false,
        errorMessage: errorMessage as string,
        claimTimestamp: Date.now(),
      });
    },
  });
}
